import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Button, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import StyledAlert from 'common/theme/alerts/StyledAlert';

const DEFAULT_ACTIONS = {};

const bottomSpacingStyles = {
  marginBottom: (theme) => theme.spacing(4)
};

const fixedVariantStyles = {
  justifyContent: 'center'
};

const getAlertObject = ({ action = null, message, severity = 'warning', styles = {} }) => ({
  action,
  message,
  severity,
  styles
});

function AlertBlock({ actions = DEFAULT_ACTIONS, onClose = null, type }) {
  const { t } = useTranslation(['availabilities', 'buttons', 'sessions']);

  const options = useMemo(
    () => ({
      allowPushNotifications: getAlertObject({
        action: (
          <Button color="inherit" onClick={actions.allow} size="small">
            {t('allow', { ns: 'buttons' })}
          </Button>
        ),
        message: t('allowPushNotifications', { ns: 'notifications' }),
        severity: 'info'
      }),
      // severity info: hereda el estilo monocromo global (superficie oscura + ícono dorado).
      availabilityDelete: getAlertObject({
        message: t('delete', { ns: 'availabilities' }),
        severity: 'info'
      }),
      availabilityRepeat: getAlertObject({
        message: t('repeat', { ns: 'availabilities' }),
        severity: 'info'
      }),
      blockedPushNotifications: getAlertObject({
        message: t('pushNotificationBlocked', { ns: 'notifications' })
      }),
      clientTimeRangeFilled: getAlertObject({
        message: t('client.timeRangeFilled', { ns: 'errors' })
      }),
      sessionDelete: getAlertObject({
        message: t('warning.delete', { ns: 'sessions' }),
        styles: bottomSpacingStyles
      }),
      sessionUpdate: getAlertObject({
        message: t('warning.update', { ns: 'sessions' }),
        styles: bottomSpacingStyles
      }),
      trainerAvailabilityOutdated: getAlertObject({
        message: t('availability.outdated', { ns: 'errors' }),
        severity: 'error',
        styles: fixedVariantStyles
      }),
      trainerProfileIncomplete: getAlertObject({
        message: t('profile.incompleteAlert', { ns: 'errors' }),
        severity: 'error',
        styles: fixedVariantStyles
      })
    }),
    [actions, t]
  );

  // MUI Alert ignora `onClose` cuando se le pasa un `action` propio (son excluyentes): por eso
  // la X del aviso de push no cerraba nada. Cuando hay ambos, la X se monta DENTRO del action.
  const { action, severity, styles } = options[type];
  const closeButton = onClose ? (
    <IconButton
      aria-label={t('close', { ns: 'buttons', defaultValue: 'Cerrar' })}
      color="inherit"
      size="small"
      onClick={onClose}
      sx={{ width: 44, height: 44 }}>
      <CloseIcon fontSize="small" />
    </IconButton>
  ) : null;

  return (
    <StyledAlert
      action={
        action || closeButton ? (
          <>
            {action}
            {closeButton}
          </>
        ) : null
      }
      severity={severity}
      sx={{ ...styles }}
      variant="filled">
      {options[type].message}
    </StyledAlert>
  );
}

AlertBlock.propTypes = {
  actions: PropTypes.objectOf(PropTypes.func),
  onClose: PropTypes.func,
  type: PropTypes.oneOf([
    'allowPushNotifications',
    'availabilityDelete',
    'availabilityRepeat',
    'blockedPushNotifications',
    'clientTimeRangeFilled',
    'sessionDelete',
    'sessionUpdate',
    'trainerAvailabilityOutdated',
    'trainerProfileIncomplete'
  ]).isRequired
};

export default AlertBlock;
